import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, Info, Loader2, X, Lock } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import CachedImg from '../components/CachedImg';


type FormatoKey = 'post' | 'story' | 'square';
type DurationMode = 'unica' | 'multiples';

const durationModeOptions: Array<{ id: DurationMode; name: string; description: string; materialIcon: string }> = [
  {
    id: 'unica',
    name: 'Escena única',
    description: 'Video corto de aproximadamente 10 segundos, ideal para una sola toma.',
    materialIcon: 'movie',
  },
  {
    id: 'multiples',
    name: 'Múltiples escenas',
    description: 'Video más largo y elaborado con varias escenas y transiciones.',
    materialIcon: 'movie_filter',
  },
];

const formatoOptions: Array<{ id: FormatoKey; name: string; image: string }> = [
  {
    id: 'post',
    name: 'Posteo (4:5)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Posteo.jpeg',
  },
  {
    id: 'story',
    name: 'Historias/Reels (9:16)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Historia.jpeg',
  },
  {
    id: 'square',
    name: 'Cuadrado (1:1)',
    image: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Cuadrado.jpeg',
  },
];


const ConfigurarVideoPersonalizado: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Accept restored state when returning from image editor
  const locationState = location.state as {
    editedImageId?: string;
    videoConfig?: { prompt: string; duration: number; formato: FormatoKey; videoType: DurationMode };
  } | null;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );

  const [prompt, setPrompt] = useState<string>(locationState?.videoConfig?.prompt || '');
  const [duration, setDuration] = useState<number>(locationState?.videoConfig?.duration || 6);
  const [formato, setFormato] = useState<FormatoKey>(locationState?.videoConfig?.formato || 'story');
  const [videoType, setVideoType] = useState<DurationMode>(locationState?.videoConfig?.videoType || 'unica');

  // Plan state
  const [userPlan, setUserPlan] = useState<string>('gratis');
  const [, setLoadingPlan] = useState<boolean>(true);
  const [userCountry, setUserCountry] = useState<string>('Argentina');
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const canAccessVideoGeneration = userPlan === 'negocio' || userPlan === 'enterprise' || userPlan === 'enterprise1' || userPlan === 'enterprise2' || userPlan === 'enterprise3';
  const isArgentina = (userCountry || '').toLowerCase() === 'argentina';

  // Menu states - Formato
  const [showFormatoMenu, setShowFormatoMenu] = useState<boolean>(false);
  const [formatoMenuAnimated, setFormatoMenuAnimated] = useState<boolean>(false);
  const [formatoMenuDragY, setFormatoMenuDragY] = useState<number>(0);
  const formatoDragStartY = useRef<number | null>(null);


  // Menu states - Video type
  const [showVideoTypeMenu, setShowVideoTypeMenu] = useState<boolean>(false);
  const [videoTypeMenuAnimated, setVideoTypeMenuAnimated] = useState<boolean>(false);
  const [videoTypeMenuDragY, setVideoTypeMenuDragY] = useState<number>(0);
  const videoTypeDragStartY = useRef<number | null>(null);

  // Video generation state
  const [generating, setGenerating] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genModerationError, setGenModerationError] = useState<boolean>(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const generatingLockRef = useRef<boolean>(false);

  // Inline video results state
  const [showResults, setShowResults] = useState(false);
  const [generatedVideoId, setGeneratedVideoId] = useState<string>('');
  const [companyIdForVideo, setCompanyIdForVideo] = useState<string>('');
  const willShowResultsRef = useRef<boolean>(false);

  // Preview image state (returned by start_video_personalizado)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [mobileImageExpanded, setMobileImageExpanded] = useState(false);
  const [generatedImageId, setGeneratedImageId] = useState<string | null>(null);
  const [generatedCompanyId, setGeneratedCompanyId] = useState<string | null>(null);

  // Video generation (step 2) state
  const [generatingVideo, setGeneratingVideo] = useState<boolean>(false);
  const [completeVideoProgressNow, setCompleteVideoProgressNow] = useState<boolean>(false);
  const generatingVideoLockRef = useRef<boolean>(false);

  // Insufficient images modal state
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState<boolean>(false);
  const [requiredImagesCount, setRequiredImagesCount] = useState<number>(24);
  const [remainingImagesCount, setRemainingImagesCount] = useState<number>(0);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);

  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  const imagesCost = duration * 2;
  const canGenerate = prompt.trim().length > 0 && !generating && !generatingVideo;
  const canGenerateVideo = !!generatedImageId && !generatingVideo && !generating;
  const hasPreview = !!(previewImageUrl || previewLoading);

  // Set mobile header title
  useEffect(() => {
    setMobileTitleOverride('Video personalizado');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  // Handle return from image editor — restore edited image as preview
  useEffect(() => {
    if (!locationState?.editedImageId) return;
    const editedId = locationState.editedImageId;
    setGeneratedImageId(editedId);

    let cancelled = false;
    const resolveImage = async () => {
      setPreviewLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        if (!profile?.id_company || cancelled) return;
        const compId = profile.id_company as string;
        setGeneratedCompanyId(compId);

        // Poll for file_name (image may still be processing)
        const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
        for (let attempt = 1; attempt <= 10; attempt++) {
          if (cancelled) return;
          const { data: imgRow } = await supabase.from('imagenes_generadas').select('file_name').eq('id', editedId).single();
          if (imgRow?.file_name) {
            const filePath = `${compId}/full/${imgRow.file_name}`;
            const { data: signed } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
            if (signed?.signedUrl && !cancelled) setPreviewImageUrl(signed.signedUrl);
            break;
          }
          if (attempt < 10) await delay(1500);
        }
      } catch (err) {
        console.error('[ConfigurarVideoPersonalizado] Error resolving edited image', err);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    resolveImage();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user plan
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoadingPlan(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) { setLoadingPlan(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan, pais')
          .eq('id_company', profile.id_company)
          .single();
        if (company?.plan) {
          setUserPlan(company.plan);
        }
        if (company?.pais) {
          setUserCountry(company.pais);
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchUserPlan();
  }, []);

  // Trigger animations after menu mounts
  useEffect(() => {
    if (showFormatoMenu) {
      requestAnimationFrame(() => setFormatoMenuAnimated(true));
    }
  }, [showFormatoMenu]);

  useEffect(() => {
    if (showVideoTypeMenu) {
      requestAnimationFrame(() => setVideoTypeMenuAnimated(true));
    }
  }, [showVideoTypeMenu]);

  // Close menu helpers
  const closeFormatoMenu = () => {
    setFormatoMenuAnimated(false);
    setTimeout(() => setShowFormatoMenu(false), 300);
  };
  const closeVideoTypeMenu = () => {
    setVideoTypeMenuAnimated(false);
    setTimeout(() => setShowVideoTypeMenu(false), 300);
  };

  // Drag handlers - Formato
  const handleFormatoTouchStart = (e: React.TouchEvent) => {
    formatoDragStartY.current = e.touches[0].clientY;
  };
  const handleFormatoTouchMove = (e: React.TouchEvent) => {
    if (formatoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - formatoDragStartY.current;
    if (deltaY > 0) setFormatoMenuDragY(deltaY);
  };
  const handleFormatoTouchEnd = () => {
    if (formatoMenuDragY > 100) {
      closeFormatoMenu();
    }
    setFormatoMenuDragY(0);
    formatoDragStartY.current = null;
  };

  // Drag handlers - Video type
  const handleVideoTypeTouchStart = (e: React.TouchEvent) => {
    videoTypeDragStartY.current = e.touches[0].clientY;
  };
  const handleVideoTypeTouchMove = (e: React.TouchEvent) => {
    if (videoTypeDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - videoTypeDragStartY.current;
    if (deltaY > 0) setVideoTypeMenuDragY(deltaY);
  };
  const handleVideoTypeTouchEnd = () => {
    if (videoTypeMenuDragY > 100) {
      closeVideoTypeMenu();
    }
    setVideoTypeMenuDragY(0);
    videoTypeDragStartY.current = null;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const resp = await fetch('https://ai-agency-dt7d.onrender.com/webhook/billing-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });
      if (!resp.ok) throw new Error('No se pudo abrir el portal');
      const data = await resp.json();
      if (data?.url) window.location.href = data.url as string;
    } catch (err) {
      console.error('Error opening billing portal:', err);
    } finally {
      setOpeningBillingPortal(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    // Plan restriction check
    if (!canAccessVideoGeneration) {
      setShowVideoPlanRestriction(true);
      generatingLockRef.current = false;
      return;
    }

    if (!prompt.trim() || generating) {
      generatingLockRef.current = false;
      return;
    }

    try {
      setGenError(null);
      setGenModerationError(false);
      setGenerating(true);
      setCompleteProgressNow(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');
      const compId = profile.id_company as string;

      // Check image quota
      const requiredImages = imagesCost;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', compId)
        .single();

      const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining < requiredImages) {
        setRequiredImagesCount(requiredImages);
        setRemainingImagesCount(remaining);
        // Compute renewal date
        const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
        const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
        if (paidStart) {
          const now = new Date();
          const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
          const anchorDay = ref.getDate();
          const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
          const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
          let start = anchorFor(now.getFullYear(), now.getMonth());
          if (start.getTime() > now.getTime()) {
            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start = anchorFor(prev.getFullYear(), prev.getMonth());
          }
          const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
          setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
        } else {
          setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
        }
        setShowInsufficientImagesModal(true);
        setGenerating(false);
        generatingLockRef.current = false;
        return;
      }

      const variantIds = ids.map(s => String(s).trim()).filter(s => !!s && s !== '0');

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds.length > 0 ? variantIds : undefined,
        modo: 'start_video_personalizado',
        mode: variantIds.length > 1 ? 'multiple' : 'single',
        prompt: prompt.trim(),
        cantidad_imagenes: 1,
        new_ui: true,
      };

      console.log('[ConfigurarVideoPersonalizado] handleGenerateVideo payload', requestData);

      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.clone().text(); } catch {}
      console.log('[ConfigurarVideoPersonalizado] response', { status: response.status, ok: response.ok, text: respText });

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[ConfigurarVideoPersonalizado] json parse error', e); }

      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const imageId = String(data.imagenes_generadas_ids[0]);
        const returnedCompanyId = (data.id_company as string) || compId;

        // Store IDs for video generation step
        setGeneratedImageId(imageId);
        setGeneratedCompanyId(returnedCompanyId);

        // Resolve image to signed URL
        setPreviewLoading(true);
        try {
          const { data: imageData } = await supabase
            .from('imagenes_generadas')
            .select('file_name')
            .eq('id', imageId)
            .single();

          if (imageData?.file_name) {
            const filePath = `${returnedCompanyId}/full/${imageData.file_name}`;
            const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
            if (signedUrlData?.signedUrl) {
              setPreviewImageUrl(signedUrlData.signedUrl);
            }
          }
        } catch (err) {
          console.error('[ConfigurarVideoPersonalizado] Error resolving preview image', err);
        } finally {
          setPreviewLoading(false);
        }

        setGenerating(false);
        generatingLockRef.current = false;
      } else if (data?.response === 'error_moderation') {
        setGenModerationError(true);
        setGenerating(false);
        generatingLockRef.current = false;
        return;
      } else {
        throw new Error('No se recibió la imagen generada.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoPersonalizado] handleGenerateVideo error', e);
      setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      setGenerating(false);
      generatingLockRef.current = false;
    }
  };

  const handleGenerateVideoFromImage = async () => {
    if (generatingVideoLockRef.current || !generatedImageId) return;
    generatingVideoLockRef.current = true;

    try {
      setGenError(null);
      setGeneratingVideo(true);
      setCompleteVideoProgressNow(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const compId = generatedCompanyId || '';

      // Check image quota before generating video
      const requiredImages = imagesCost;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', compId)
        .single();

      const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining < requiredImages) {
        setRequiredImagesCount(requiredImages);
        setRemainingImagesCount(remaining);
        const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
        const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
        if (paidStart) {
          const now = new Date();
          const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
          const anchorDay = ref.getDate();
          const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
          const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
          let start = anchorFor(now.getFullYear(), now.getMonth());
          if (start.getTime() > now.getTime()) {
            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start = anchorFor(prev.getFullYear(), prev.getMonth());
          }
          const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
          setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
        } else {
          setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
        }
        setShowInsufficientImagesModal(true);
        setGeneratingVideo(false);
        generatingVideoLockRef.current = false;
        return;
      }

      const formatMap: Record<string, string> = { story: '9:16', square: '1:1', post: '4:5' };

      const variantIds = ids.map(s => String(s).trim()).filter(s => !!s && s !== '0');

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds.length > 0 ? variantIds : undefined,
        tipo_video: 'cinematografico',
        prompt: prompt.trim(),
        duracion: duration,
        Format: formatMap[formato] || '9:16',
        style_prompt: '',
        multiples_escenas: videoType === 'multiples',
        origen: 'video personalizado',
        scenes: [{
          index: 0,
          prompt: prompt.trim(),
          start_frame: generatedImageId,
          end_frame: null,
          duration_seconds: duration,
        }],
      };

      console.log('[ConfigurarVideoPersonalizado] handleGenerateVideoFromImage payload', requestData);

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/video_cinema', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.text(); } catch {}
      console.log('[ConfigurarVideoPersonalizado] video_cinema response', { status: response.status, ok: response.ok, text: respText });

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = respText ? JSON.parse(respText) : null; } catch (e) { console.error('[ConfigurarVideoPersonalizado] json parse error', e); }

      if (data?.response === 'success' && (data?.video_id || data?.video_generado_id)) {
        const videoId = (data.video_id || data.video_generado_id) as string;
        willShowResultsRef.current = true;
        setGeneratedVideoId(videoId);
        setCompanyIdForVideo(compId);
        setCompleteVideoProgressNow(true);
      } else {
        throw new Error('No se recibió el video generado.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoPersonalizado] handleGenerateVideoFromImage error', e);
      setGenError('Se produjo un error al generar el video. Por favor, volvé a intentarlo.');
      setGeneratingVideo(false);
      generatingVideoLockRef.current = false;
    }
  };

  const handleEditImage = () => {
    if (!generatedImageId || !generatedCompanyId) return;
    const returnUrl = location.pathname + location.search;
    navigate(`/redes-sociales/editar-imagen?id=${generatedImageId}&companyId=${generatedCompanyId}`, {
      state: {
        returnToVideoPersonalizado: true,
        returnUrl,
        videoPersonalizadoParams: { ids: idsParam, companyId: generatedCompanyId },
        videoConfig: { prompt, duration, formato, videoType },
      },
    });
  };

  if (showResults) {
    return (
      <GeneratedImagesInline
        imageIds={[generatedVideoId]}
        companyId={companyIdForVideo}
        onClose={() => {
          setShowResults(false);
        }}
        onReturnToConfig={() => {
          setShowResults(false);
        }}
        onReady={() => {}}
      />
    );
  }

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove('tap-flash');
    void el.offsetWidth;
    el.classList.add('tap-flash');
  };

  return (
    <div className="min-h-[100dvh] md:min-h-screen bg-[#F2F2F7]">
      {/* Desktop sticky header */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className={`${hasPreview ? 'max-w-6xl' : 'max-w-4xl'} mx-auto px-8`}>
            <div className="flex items-center justify-between h-14">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Video personalizado</h1>
              {generatedImageId ? (
                <button
                  type="button"
                  onClick={handleGenerateVideoFromImage}
                  disabled={!canGenerateVideo}
                  className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-[10px] text-[15px] font-medium transition-all ${
                    !canGenerateVideo
                      ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                      : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
                  }`}
                >
                  {generatingVideo ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando video...
                    </>
                  ) : 'Generar video'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleGenerateVideo}
                  disabled={!canGenerate}
                  className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-[10px] text-[15px] font-medium transition-all ${
                    !canGenerate
                      ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                      : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
                  }`}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : 'Generar previsualización'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className={`hidden sm:block ${hasPreview ? 'max-w-6xl' : 'max-w-4xl'} mx-auto px-8 pt-[72px] pb-6`}>
        <div className={hasPreview ? 'flex gap-6' : ''}>
          {/* Preview image — desktop (left column, sticky) */}
          {hasPreview && (
            <div className="w-[400px] shrink-0 sticky top-[80px] self-start">
              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Vista previa</p>
              <div className="rounded-lg overflow-hidden bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                <div className="aspect-[9/16] relative">
                  {previewLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#1644EB]/40" />
                    </div>
                  ) : previewImageUrl ? (
                    <img
                      src={previewImageUrl}
                      alt="Vista previa"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Options column */}
          <div className="flex-1 min-w-0">
            {/* Prompt — iOS grouped list */}
            <div className={hasPreview ? 'mt-0' : 'mt-4'}>
              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Descripción del video</p>
              <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                <div className="px-4 py-3">
                  <textarea
                    rows={5}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Ej: Un video cinematográfico mostrando el producto sobre una mesa de mármol, con iluminación suave y movimiento de cámara lento hacia adelante..."
                    className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
                  />
                </div>
              </div>
              <p className="text-[13px] text-[#3C3C43]/60 mt-2 px-4">Cuanto más específico seas, mejor será el resultado.</p>
            </div>

            {/* Configuración — iOS grouped list */}
            <div className="mt-6">
              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Configuración</p>
              <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                {/* Tipo de video */}
                <button
                  type="button"
                  onClick={() => { closeFormatoMenu(); setShowVideoTypeMenu(true); }}
                  onClickCapture={tapFlash}
                  className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">theaters</span>
                    <span className="text-[15px] text-brand-black/90">Tipo de video</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-[15px] text-[#3C3C43]/60">{durationModeOptions.find(o => o.id === videoType)?.name || 'Escena única'}</span>
                    <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                  </span>
                </button>

                <div className="ml-[46px] h-px bg-black/[0.08]" />

                {/* Duración — slider */}
                <div className="px-4 py-[11px]">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">timer</span>
                    <span className="text-[15px] text-brand-black/90">Duración</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 flex flex-col">
                      <div className="relative h-[22px] flex items-center">
                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                        <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]" style={{ width: `${((duration - 4) / 11) * 100}%` }} />
                        <input
                          type="range"
                          min={4}
                          max={15}
                          step={1}
                          value={duration}
                          onChange={(e) => setDuration(Number(e.target.value))}
                          className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                        />
                      </div>
                      <div className="flex justify-between px-[2px] mt-1">
                        {Array.from({ length: 12 }, (_, i) => (
                          <div key={i} className={`w-[3px] h-[3px] rounded-full ${i + 4 <= duration ? 'bg-[#1644EB]/40' : 'bg-neutral-300'}`} />
                        ))}
                      </div>
                    </div>
                    <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">{duration} seg</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                    <span className="text-[13px] text-[#3C3C43]/60">Se consumirán {imagesCost} imágenes</span>
                  </div>
                </div>

                <div className="ml-[46px] h-px bg-black/[0.08]" />

                {/* Formato */}
                <button
                  type="button"
                  onClick={() => { closeVideoTypeMenu(); setShowFormatoMenu(true); }}
                  onClickCapture={tapFlash}
                  className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">aspect_ratio</span>
                    <span className="text-[15px] text-brand-black/90">Formato</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-[15px] text-[#3C3C43]/60">{formatoOptions.find(o => o.id === formato)?.name || 'Historias/Reels (9:16)'}</span>
                    <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                  </span>
                </button>
              </div>
            </div>

            {/* Editar imagen — desktop */}
            {generatedImageId && (
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={handleEditImage}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 hover:bg-neutral-50 transition-all"
                >
                  <span className="material-symbols-rounded text-[18px]">edit</span>
                  Editar imagen
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="sm:hidden">
        {/* Preview image — mobile (centered, tap to expand) */}
        {hasPreview && (
          <div className="px-4 pt-4 flex justify-center">
            <div
              className="cursor-pointer"
              style={{
                width: mobileImageExpanded ? '80%' : 'calc(50% - 4px)',
                transition: 'width 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)',
              }}
              onClick={() => setMobileImageExpanded(prev => !prev)}
            >
              <div className="rounded-lg overflow-hidden bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                <div className="aspect-[9/16] relative">
                  {previewLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#1644EB]/40" />
                    </div>
                  ) : previewImageUrl ? (
                    <img
                      src={previewImageUrl}
                      alt="Vista previa"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Editar imagen button — mobile */}
        {generatedImageId && (
          <div className="px-4 mt-3">
            <button
              type="button"
              onClick={handleEditImage}
              className="w-full h-10 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 active:bg-neutral-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <span className="material-symbols-rounded text-[18px]">edit</span>
              Editar imagen
            </button>
          </div>
        )}

        {/* Options */}
        <div className="px-4 pt-1 pb-28">
          {/* Prompt — iOS grouped list */}
          <div className="mt-2">
            <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Descripción del video</p>
            <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              <div className="px-4 py-3">
                <textarea
                  rows={5}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Ej: Un video cinematográfico mostrando el producto sobre una mesa de mármol, con iluminación suave y movimiento de cámara lento hacia adelante..."
                  className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
                />
              </div>
            </div>
            <p className="text-[13px] text-[#3C3C43]/60 mt-2 px-4">Cuanto más específico seas, mejor será el resultado.</p>
          </div>

          {/* Configuración — iOS grouped list */}
          <div className="mt-6">
            <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Configuración</p>
            <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              {/* Tipo de video */}
              <button
                type="button"
                onClick={() => { closeFormatoMenu(); setShowVideoTypeMenu(true); }}
                onClickCapture={tapFlash}
                className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">theaters</span>
                  <span className="text-[15px] text-brand-black/90">Tipo de video</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-[15px] text-[#3C3C43]/60">{durationModeOptions.find(o => o.id === videoType)?.name || 'Escena única'}</span>
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                </span>
              </button>

              <div className="ml-[46px] h-px bg-black/[0.08]" />

              {/* Duración — slider */}
              <div className="px-4 py-[11px]">
                <div className="flex items-center gap-3 mb-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">timer</span>
                  <span className="text-[15px] text-brand-black/90">Duración</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 flex flex-col">
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                      <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]" style={{ width: `${((duration - 4) / 11) * 100}%` }} />
                      <input
                        type="range"
                        min={4}
                        max={15}
                        step={1}
                        value={duration}
                        onChange={(e) => setDuration(Number(e.target.value))}
                        className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                      />
                    </div>
                    <div className="flex justify-between px-[2px] mt-1">
                      {Array.from({ length: 12 }, (_, i) => (
                        <div key={i} className={`w-[3px] h-[3px] rounded-full ${i + 4 <= duration ? 'bg-[#1644EB]/40' : 'bg-neutral-300'}`} />
                      ))}
                    </div>
                  </div>
                  <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">{duration} seg</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
                  <span className="text-[13px] text-[#3C3C43]/60">Se consumirán {imagesCost} imágenes</span>
                </div>
              </div>

              <div className="ml-[46px] h-px bg-black/[0.08]" />

              {/* Formato */}
              <button
                type="button"
                onClick={() => { closeVideoTypeMenu(); setShowFormatoMenu(true); }}
                onClickCapture={tapFlash}
                className="w-full px-4 py-[11px] flex items-center justify-between transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">aspect_ratio</span>
                  <span className="text-[15px] text-brand-black/90">Formato</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-[15px] text-[#3C3C43]/60">{formatoOptions.find(o => o.id === formato)?.name || 'Historias/Reels (9:16)'}</span>
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                </span>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile bottom bar — frosted glass */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        {!generatedImageId && (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60 mb-2">
            <Info className="w-3.5 h-3.5 text-[#1644EB]" />
            <span>Se consumirán {imagesCost} imágenes</span>
          </div>
        )}
        {generatedImageId ? (
          <button
            type="button"
            onClick={handleGenerateVideoFromImage}
            disabled={!canGenerateVideo}
            className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
              !canGenerateVideo ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed' : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
            }`}
          >
            {generatingVideo ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Generando video...
              </span>
            ) : 'Generar video'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleGenerateVideo}
            disabled={!canGenerate}
            className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
              !canGenerate ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed' : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
            }`}
          >
            {generating ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Generando...
              </span>
            ) : 'Generar previsualización'}
          </button>
        )}
      </div>

      {/* Desktop backdrop overlay when side panel is open */}
      {(showFormatoMenu || showVideoTypeMenu) && (
        <div
          className="hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer"
          onClick={() => { closeFormatoMenu(); closeVideoTypeMenu(); }}
        />
      )}

      {/* Video Type Menu */}
      {showVideoTypeMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              videoTypeMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Tipo de video</h3>
              <button type="button" onClick={closeVideoTypeMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-1">
                {durationModeOptions.map((opt) => {
                  const selected = videoType === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setVideoType(opt.id); closeVideoTypeMenu(); }}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${
                        selected ? 'bg-neutral-100' : ''
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                        selected ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        <span className="material-symbols-rounded text-[18px]">{opt.materialIcon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-neutral-900">{opt.name}</p>
                        <p className={`text-[13px] ${selected ? 'text-neutral-600' : 'text-neutral-400'}`}>{opt.description}</p>
                      </div>
                      {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closeVideoTypeMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              <button type="button" onClick={closeVideoTypeMenu} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">Aceptar</button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${videoTypeMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
                onClick={closeVideoTypeMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: 'calc(100dvh - 1rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: videoTypeMenuDragY > 0
                    ? `translateY(${videoTypeMenuDragY}px)`
                    : videoTypeMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: videoTypeMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleVideoTypeTouchStart}
                  onTouchMove={handleVideoTypeTouchMove}
                  onTouchEnd={handleVideoTypeTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closeVideoTypeMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Tipo de video</h3>
                  <button type="button" onClick={closeVideoTypeMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="space-y-1">
                    {durationModeOptions.map((opt) => {
                      const selected = videoType === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => { setVideoType(opt.id); closeVideoTypeMenu(); }}
                          className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 rounded-md ${
                            selected ? 'bg-neutral-100' : ''
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                            selected ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'
                          }`}>
                            <span className="material-symbols-rounded text-[18px]">{opt.materialIcon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-medium text-neutral-900">{opt.name}</p>
                            <p className={`text-[13px] ${selected ? 'text-neutral-600' : 'text-neutral-400'}`}>{opt.description}</p>
                          </div>
                          {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
        </>
      )}

      {/* Formato Menu */}
      {showFormatoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              formatoMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Formato</h3>
              <button type="button" onClick={closeFormatoMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                {formatoOptions.map(opt => {
                  const selected = formato === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setFormato(opt.id); closeFormatoMenu(); }}
                      className={`w-full rounded-[13px] p-3 transition-colors focus:outline-none flex items-center gap-4 ${
                        selected
                          ? 'bg-neutral-100 ring-2 ring-[#1644EB]'
                          : 'bg-neutral-50 hover:bg-neutral-100'
                      }`}
                    >
                      <div className="relative overflow-hidden rounded-[10px] w-20 h-20 shrink-0">
                        <CachedImg src={opt.image} alt={opt.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <p className="text-[15px] text-neutral-900 font-medium">{opt.name}</p>
                        {selected && <Check className="w-4 h-4 text-brand-primary shrink-0 mr-1" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closeFormatoMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              <button type="button" onClick={closeFormatoMenu} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">Aceptar</button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${formatoMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
                onClick={closeFormatoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: 'calc(100dvh - 1rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: formatoMenuDragY > 0
                    ? `translateY(${formatoMenuDragY}px)`
                    : formatoMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: formatoMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleFormatoTouchStart}
                  onTouchMove={handleFormatoTouchMove}
                  onTouchEnd={handleFormatoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closeFormatoMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Formato</h3>
                  <button type="button" onClick={closeFormatoMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    {formatoOptions.map(opt => {
                      const selected = formato === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => { setFormato(opt.id); closeFormatoMenu(); }}
                          className={`relative rounded-[13px] overflow-hidden transition-all ${
                            selected ? 'ring-2 ring-[#1644EB]' : 'ring-1 ring-black/[0.06] active:bg-black/[0.02]'
                          }`}
                        >
                          <div className="bg-white px-4 pt-4">
                            <CachedImg src={opt.image} alt={opt.name} className="w-full h-40 object-cover rounded-[10px]" />
                          </div>
                          {selected && (
                            <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <p className="text-[13px] text-brand-black/90 px-3 py-2.5 font-medium text-center bg-white">{opt.name}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
        </>
      )}

      {/* Loading Animation — Image generation (step 1) */}
      {generating && (
        <LoadingAnimation
          message="Generando imagen..."
          subMessage="Esto puede tardar unos segundos. No cierres esta ventana."
          durationMs={50000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setGenerating(false);
            generatingLockRef.current = false;
          }}
        />
      )}

      {/* Loading Animation — Video generation (step 2) */}
      {generatingVideo && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar unos minutos. No cierres esta ventana."
          durationMs={90000}
          completeNow={completeVideoProgressNow}
          showCountdown
          onCompleted={() => {
            if (willShowResultsRef.current) {
              willShowResultsRef.current = false;
              setShowResults(true);
            }
            setGeneratingVideo(false);
            generatingVideoLockRef.current = false;
          }}
        />
      )}

      {/* Error toast */}
      {genError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Error al generar</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
            </div>
            <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <span className="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Moderation error toast */}
      {genModerationError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Contenido no permitido</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">No se puede generar videos con este tipo de contenido. No se consumieron imágenes.</p>
            </div>
            <button type="button" onClick={() => setGenModerationError(false)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <span className="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Insufficient Images Modal */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para generar video
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para generar este video necesitás <strong>{requiredImagesCount} imágenes</strong>, pero {remainingImagesCount === 0 ? <>no te quedan imágenes restantes este mes</> : <>solo tenés <strong>{remainingImagesCount}</strong> restantes este mes</>}.
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para generar videos ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowInsufficientImagesModal(false); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setShowInsufficientImagesModal(false)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Video plan restriction modal */}
      {showVideoPlanRestriction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
          <div className="bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md relative flex flex-col sm:flex-row sm:overflow-hidden">
            <button type="button" onClick={() => setShowVideoPlanRestriction(false)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 inline-flex items-center justify-center transition-colors" aria-label="Cerrar"><X className="w-5 h-5" /></button>
            <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">Función exclusiva</h3>
            <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
              <video src="/images/video_animar_imagen.mp4" autoPlay loop muted playsInline className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg" />
              <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden"><Lock className="w-5 h-5 text-white" /></div>
            </div>
            <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
              <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">Función exclusiva</h3>
              <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">La generación de videos está disponible únicamente para los usuarios de los planes <span className="font-semibold text-brand-black/80">Pro</span> y <span className="font-semibold text-brand-black/80">Ultra</span>.</p>
              <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
                {userPlan === 'gratis' ? (
                  <button type="button" onClick={() => { setShowVideoPlanRestriction(false); navigate('/subscription'); }} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">Ver planes</button>
                ) : isArgentina ? (
                  <button type="button" onClick={() => { setShowVideoPlanRestriction(false); navigate('/subscription'); }} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">Mejorar plan</button>
                ) : (
                  <button type="button" onClick={handleOpenBillingPortal} disabled={openingBillingPortal} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">{openingBillingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mejorar plan'}</button>
                )}
                <button type="button" onClick={() => setShowVideoPlanRestriction(false)} className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurarVideoPersonalizado;
